import { LogLevel } from '@hdms/shared/common-types';

import { circularSafeStringify } from '../helpers/object-utils';
import { PlatformType } from '../platform/platform.service';

import { LoggerConfig, OutputKeys, OutputParams } from './logger.types';

type Instances = { [key in PlatformType]?: Logger };

export class Logger {
  private static loggerConfig: LoggerConfig;
  private static readonly instances: Instances = {};
  private static defaultLogger: Logger;

  constructor(private readonly platformType?: PlatformType) {}

  //region Static
  static init(loggerConfig: LoggerConfig): void {
    if (this.loggerConfig) {
      return;
    }

    const logLevel = loggerConfig.logLevel;
    const logLevelString = LogLevel[logLevel] ?? LogLevel.OFF;

    this.loggerConfig = {
      isProduction: loggerConfig.isProduction,
      platformType: loggerConfig.platformType,
      logLevel: typeof logLevel === 'number' ? logLevel : logLevelString
    };

    this.defaultLogger = new Logger();
  }

  static get browser(): Logger {
    if (!this.instances[PlatformType.BROWSER]) {
      this.instances[PlatformType.BROWSER] = new Logger(PlatformType.BROWSER);
    }

    return this.instances[PlatformType.BROWSER];
  }

  static get server(): Logger {
    if (!this.instances[PlatformType.SERVER]) {
      this.instances[PlatformType.SERVER] = new Logger(PlatformType.SERVER);
    }

    return this.instances[PlatformType.SERVER];
  }

  static debug(...outputParams: OutputParams[]): void {
    this.defaultLogger?.debug(...outputParams);
  }

  static info(...outputParams: OutputParams[]): void {
    this.defaultLogger?.info(...outputParams);
  }

  static log(...outputParams: OutputParams[]): void {
    this.defaultLogger?.log(...outputParams);
  }

  static warn(...outputParams: OutputParams[]): void {
    this.defaultLogger?.warn(...outputParams);
  }

  static error(...outputParams: OutputParams[]): void {
    this.defaultLogger?.error(...outputParams);
  }

  private static parseOutput(output: Record<OutputKeys, LogLevel | OutputParams[] | PlatformType>): void {
    const logLevel = output.logLevel as LogLevel;
    const platformType = output.platformType as PlatformType;

    if (!this.canOutput(logLevel, platformType)) {
      return;
    }

    const outputParams = this.processOutputParams(logLevel, output.outputParams as OutputParams[]);

    this.outputToConsole(logLevel, outputParams);
  }

  private static canOutput(logLevel: LogLevel, platformType: PlatformType): boolean {
    if (!this.loggerConfig || this.loggerConfig.logLevel > logLevel) {
      return false;
    }

    return this.canPlatformOutput(platformType);
  }

  private static canPlatformOutput(platformType: PlatformType): boolean {
    return !platformType || this.loggerConfig.platformType === platformType;
  }

  private static processOutputParams(logLevel: LogLevel, outputParams: OutputParams[]): OutputParams[] {
    return this.isProdServer() ? this.formatProdServerParams(logLevel, outputParams) : outputParams;
  }

  private static isProdServer(): boolean {
    return this.loggerConfig.isProduction && this.loggerConfig.platformType === PlatformType.SERVER;
  }

  private static formatProdServerParams(logLevel: LogLevel, outputParams: OutputParams[]): string[] {
    return [
      circularSafeStringify({
        message: outputParams.length === 1 ? outputParams[0] : outputParams,
        level: LogLevel[logLevel].toLowerCase()
      })
    ];
  }

  private static outputToConsole(logLevel: LogLevel, outputParams: OutputParams[]): void {
    /* eslint-disable no-console, no-restricted-syntax */
    switch (logLevel) {
      case LogLevel.DEBUG:
        console.debug(...outputParams);
        break;
      case LogLevel.INFO:
        console.info(...outputParams);
        break;
      case LogLevel.LOG:
        console.log(...outputParams);
        break;
      case LogLevel.WARN:
        console.warn(...outputParams);
        break;
      case LogLevel.ERROR:
        console.error(...outputParams);
        break;
    }
    /* eslint-enable no-console, no-restricted-syntax */
  }
  //endregion

  //region Instance
  debug(...outputParams: OutputParams[]): void {
    Logger.parseOutput({ logLevel: LogLevel.DEBUG, platformType: this.platformType, outputParams });
  }

  info(...outputParams: OutputParams[]): void {
    Logger.parseOutput({ logLevel: LogLevel.INFO, platformType: this.platformType, outputParams });
  }

  log(...outputParams: OutputParams[]): void {
    Logger.parseOutput({ logLevel: LogLevel.LOG, platformType: this.platformType, outputParams });
  }

  warn(...outputParams: OutputParams[]): void {
    Logger.parseOutput({ logLevel: LogLevel.WARN, platformType: this.platformType, outputParams });
  }

  error(...outputParams: OutputParams[]): void {
    Logger.parseOutput({ logLevel: LogLevel.ERROR, platformType: this.platformType, outputParams });
  }
  //endregion
}
